import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Truck, Phone, User, FileText, MapPin, Building2, CreditCard, Camera, AlertCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', license_number: '', license_photo: '',
    address: '', city: '', state: '', pincode: '', address_proof_type: '', address_proof_photo: '',
    vehicle_number: '', rc_photo: '', bank_account_number: '', bank_ifsc: '',
    bank_name: '', bank_branch: '', account_holder_name: '',
    emergency_contact_name: '', emergency_contact_number: ''
  });

  const handlePhotoUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('File size should be less than 5MB'); return; }
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, [field]: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (s) => {
    if (s === 1 && (!formData.name || !formData.mobile || !formData.email)) { toast.error('Please fill all required fields'); return false; }
    if (s === 1 && !/^[0-9]{10}$/.test(formData.mobile)) { toast.error('Enter valid 10-digit mobile'); return false; }
    if (s === 2 && !formData.license_number) { toast.error('License number required'); return false; }
    if (s === 3 && (!formData.address || !formData.city || !formData.state || !formData.pincode)) { toast.error('Fill all address fields'); return false; }
    if (s === 4 && !formData.vehicle_number) { toast.error('Vehicle number required'); return false; }
    if (s === 5 && (!formData.bank_account_number || !formData.bank_ifsc || !formData.account_holder_name)) { toast.error('Fill all bank details'); return false; }
    return true;
  };

  const handleNext = () => { if (validateStep(step)) setStep(step + 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(step)) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/driver/register`, formData);
      toast.success('Registration successful! Waiting for admin approval.');
      setTimeout(() => navigate('/mobileapp/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  const steps = [
    // Step 1: Personal Info
    <div key="1" className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">Personal Information</h3>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Full Name *</Label>
        <div className="relative"><User className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-name-input" placeholder="Enter your full name" className="pl-10 h-12 bg-slate-50" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Mobile Number *</Label>
        <div className="relative"><Phone className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-mobile-input" type="tel" placeholder="10-digit mobile" className="pl-10 h-12 bg-slate-50" value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Email *</Label>
        <div className="relative"><FileText className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-email-input" type="email" placeholder="your.email@example.com" className="pl-10 h-12 bg-slate-50" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Emergency Contact Name</Label>
        <Input placeholder="Emergency contact person" className="h-12 bg-slate-50" value={formData.emergency_contact_name} onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})} />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Emergency Contact Number</Label>
        <Input type="tel" placeholder="Emergency contact mobile" className="h-12 bg-slate-50" value={formData.emergency_contact_number} onChange={(e) => setFormData({...formData, emergency_contact_number: e.target.value})} />
      </div>
    </div>,
    // Step 2: License
    <div key="2" className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">License Details</h3>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Driving License Number *</Label>
        <div className="relative"><FileText className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-license-input" placeholder="DL-XXXXXXXXXX" className="pl-10 h-12 bg-slate-50" value={formData.license_number} onChange={(e) => setFormData({...formData, license_number: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Upload License Photo</Label>
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50">
          <input id="license-photo" data-testid="license-photo-input" type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'license_photo')} className="hidden" />
          <label htmlFor="license-photo" className="cursor-pointer">
            {formData.license_photo ? <img src={formData.license_photo} alt="License" className="max-h-32 mx-auto rounded" /> :
              <div className="space-y-2"><Camera className="w-12 h-12 mx-auto text-slate-400" /><p className="text-sm text-slate-600">Click to upload</p></div>}
          </label>
        </div>
      </div>
    </div>,
    // Step 3: Address
    <div key="3" className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">Address Information</h3>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Full Address *</Label>
        <div className="relative"><MapPin className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-address-input" placeholder="House/Flat No., Street, Area" className="pl-10 h-12 bg-slate-50" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label className="text-sm font-medium">City *</Label><Input placeholder="City" className="h-12 bg-slate-50" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} required /></div>
        <div className="space-y-2"><Label className="text-sm font-medium">State *</Label><Input placeholder="State" className="h-12 bg-slate-50" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} required /></div>
      </div>
      <div className="space-y-2"><Label className="text-sm font-medium">Pincode *</Label><Input placeholder="6-digit pincode" className="h-12 bg-slate-50" value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} required /></div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Address Proof Type</Label>
        <Select value={formData.address_proof_type} onValueChange={(v) => setFormData({...formData, address_proof_type: v})}>
          <SelectTrigger className="h-12 bg-slate-50"><SelectValue placeholder="Select document type" /></SelectTrigger>
          <SelectContent><SelectItem value="aadhar">Aadhar Card</SelectItem><SelectItem value="passport">Passport</SelectItem><SelectItem value="voter">Voter ID</SelectItem><SelectItem value="utility">Utility Bill</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Upload Address Proof</Label>
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50">
          <input id="address-proof" type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'address_proof_photo')} className="hidden" />
          <label htmlFor="address-proof" className="cursor-pointer">
            {formData.address_proof_photo ? <img src={formData.address_proof_photo} alt="Proof" className="max-h-32 mx-auto rounded" /> :
              <div className="space-y-2"><Camera className="w-12 h-12 mx-auto text-slate-400" /><p className="text-sm text-slate-600">Click to upload</p></div>}
          </label>
        </div>
      </div>
    </div>,
    // Step 4: Vehicle
    <div key="4" className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">Vehicle Details</h3>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Vehicle Registration Number *</Label>
        <div className="relative"><Truck className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="register-vehicle-input" placeholder="MH-01-AB-1234" className="pl-10 h-12 bg-slate-50" value={formData.vehicle_number} onChange={(e) => setFormData({...formData, vehicle_number: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Upload RC</Label>
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50">
          <input id="rc-photo" data-testid="rc-photo-input" type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'rc_photo')} className="hidden" />
          <label htmlFor="rc-photo" className="cursor-pointer">
            {formData.rc_photo ? <img src={formData.rc_photo} alt="RC" className="max-h-32 mx-auto rounded" /> :
              <div className="space-y-2"><Camera className="w-12 h-12 mx-auto text-slate-400" /><p className="text-sm text-slate-600">Click to upload RC photo</p></div>}
          </label>
        </div>
      </div>
    </div>,
    // Step 5: Bank
    <div key="5" className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900">Bank Account Details</h3>
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start space-x-2">
        <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-orange-800">Bank details required for salary/payment transfers</p>
      </div>
      <div className="space-y-2"><Label className="text-sm font-medium">Account Holder Name *</Label><Input data-testid="account-holder-input" placeholder="As per bank records" className="h-12 bg-slate-50" value={formData.account_holder_name} onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})} required /></div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">Bank Account Number *</Label>
        <div className="relative"><CreditCard className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="account-number-input" placeholder="Enter account number" className="pl-10 h-12 bg-slate-50" value={formData.bank_account_number} onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})} required />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-medium">IFSC Code *</Label>
        <div className="relative"><Building2 className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input data-testid="ifsc-input" placeholder="11-character IFSC" className="pl-10 h-12 bg-slate-50" value={formData.bank_ifsc} onChange={(e) => setFormData({...formData, bank_ifsc: e.target.value.toUpperCase()})} maxLength="11" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label className="text-sm font-medium">Bank Name</Label><Input placeholder="Bank name" className="h-12 bg-slate-50" value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})} /></div>
        <div className="space-y-2"><Label className="text-sm font-medium">Branch</Label><Input placeholder="Branch name" className="h-12 bg-slate-50" value={formData.bank_branch} onChange={(e) => setFormData({...formData, bank_branch: e.target.value})} /></div>
      </div>
    </div>
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="w-full max-w-2xl shadow-xl border-slate-100">
        <CardHeader className="text-center space-y-3 pb-6 border-b border-slate-100">
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>PrimeSwift Driver</CardTitle>
          <CardDescription className="text-base">Registration - Step {step} of 5</CardDescription>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-4">
            <div className="bg-orange-500 h-2 rounded-full transition-all duration-300" style={{ width: `${(step / 5) * 100}%` }} />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={step === 5 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
            {steps[step - 1]}
            <div className="flex space-x-3 mt-6">
              {step > 1 && <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="flex-1 h-12 rounded-full" data-testid="back-button">Back</Button>}
              <Button type="submit" data-testid={step === 5 ? "register-submit-button" : "next-button"} className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white text-base font-semibold rounded-full" disabled={loading}>
                {loading ? 'Submitting...' : step === 5 ? 'Complete Registration' : 'Next'}
              </Button>
            </div>
            {step === 1 && (
              <div className="text-center pt-4">
                <button type="button" onClick={() => navigate('/mobileapp')} className="text-sm text-slate-600 hover:text-slate-900 block w-full">Already registered? Login here</button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverRegister;
